import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'

import { Menu, MenuChild } from '../components'
import { useBindDispatch, useScreenSize, useAppSelector } from '../hooks'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getStaffRouterKey, STAFF_ROLES } from 'helpers'
import { useNavBarConfig } from 've/hooks/useNavBarConfig'
import { useNavBarConfig as useFcNavBarConfig } from 'fc/hooks/useNavBarConfig'

const emptyNavConfig = { mobileMenuConfig: [], desktopMenuConfig: [] }

const getNavConfigForRole = (routerKey, fcNavConfig, veNavConfig) => {
    if (routerKey === STAFF_ROLES.FC) {
        return fcNavConfig
    }
    if (routerKey === STAFF_ROLES.VE) {
        return veNavConfig
    }
    return emptyNavConfig
}

const Nav = ({ user }) => {
    const screenSize = useScreenSize()
    const {
        getUserPermissionHandler,
        clearClientSearchHandler,
        showWarningModalHandler,
        fetchUnreadNotificationsHandler,
        filterSelectorHandler
    } = useBindDispatch()
    const location = useLocation()
    const [permissionsState, setPermissions] = useState([])
    const isMobile = screenSize.screenWidth <= 800
    const { username, role } = user || {}

    const { permissions } = useSelector(
        ({ userPermissions }) => userPermissions
    )

    const { isVisited } = useAppSelector((state) => state.isVisited)
    const { hasUnreadNotifications } = useAppSelector(
        (state) => state.notifications
    )

    const navBarParams = {
        permissionsState,
        hasUnreadNotifications,
        clearClientSearchHandler,
        clearSelectedFilter: filterSelectorHandler
    }
    // Always call both hooks (rules of hooks). Select by role after.
    const veNavConfig = useNavBarConfig(navBarParams)
    const fcNavConfig = useFcNavBarConfig(navBarParams)
    const routerKey = getStaffRouterKey(role)
    const { mobileMenuConfig, desktopMenuConfig } = getNavConfigForRole(
        routerKey,
        fcNavConfig,
        veNavConfig
    )

    useEffect(() => {
        if (username) {
            fetchUnreadNotificationsHandler()
        }
    }, [username])

    const hasFetchedPermissionsRef = useRef(false)

    useEffect(() => {
        if (
            permissions &&
            permissions.length > 0 &&
            !hasFetchedPermissionsRef.current
        ) {
            setPermissions(permissions)
            hasFetchedPermissionsRef.current = true
        }

        if (!hasFetchedPermissionsRef.current) {
            getUserPermissionHandler()
        }
    }, [permissions?.length])

    const isVisitedRoute = location.pathname === '/visit'

    const visitWarningHandler = (handlerOnClickCallback) => {
        if (isVisitedRoute) {
            showWarningModalHandler(true)
            return
        }

        return handlerOnClickCallback && handlerOnClickCallback()
    }

    const mobileMenu = (
        <>
            {mobileMenuConfig.map(
                (
                    {
                        name,
                        path,
                        href,
                        icon,
                        disableActive,
                        menuOption,
                        onClickCallback,
                        isVisible
                    },
                    idx
                ) => (
                    <MenuChild
                        icon={icon}
                        href={href}
                        path={path}
                        key={idx}
                        onClickCallback={
                            !isVisited
                                ? () => visitWarningHandler(onClickCallback)
                                : onClickCallback
                        }
                        disableActive={disableActive}
                        menuOption={isVisitedRoute ? '' : menuOption}
                        isVisible={{ isVisible, idx }}
                        id={`${path}-menu`}
                    >
                        {name}
                    </MenuChild>
                )
            )}
        </>
    )

    const desktopMenu = (
        <>
            {desktopMenuConfig
                .filter((config) => config.isVisible)
                .map(
                    (
                        {
                            name,
                            path,
                            href,
                            icon,
                            disableActive,
                            onClickCallback,
                            isVisible
                        },
                        idx
                    ) => {
                        return (
                            <MenuChild
                                icon={icon}
                                href={href}
                                onClickCallback={
                                    !isVisited
                                        ? () =>
                                              visitWarningHandler(
                                                  onClickCallback
                                              )
                                        : onClickCallback
                                }
                                path={path}
                                key={idx}
                                disableActive={disableActive}
                                id={`${path}-menu`}
                                isVisible={{ isVisible, idx }}
                            >
                                {name}
                            </MenuChild>
                        )
                    }
                )}
        </>
    )

    return (
        <Menu type={isMobile ? 'mobile menu' : 'top menu'}>
            {isMobile && mobileMenu ? mobileMenu : desktopMenu}
        </Menu>
    )
}

Nav.propTypes = {
    user: PropTypes.shape({
        username: PropTypes.string,
        role: PropTypes.string
    })
}

export default Nav
